import React, { useLayoutEffect, useRef, useState } from 'react';
import SubPage from '../components/site/SubPage';
import {
  ClosingBrand,
  PageSection,
  PageTitleBox,
  SectionHeading,
  SourceLine,
} from '../components/site/SubPageParts';

const IMG = '/images/site/bad-dream';

/*
 * All three blocks come from the 2025 IC3 Annual Report, Cryptocurrency Fraud
 * Trends (p.53). The mockup repeated the investment-fraud figures under all
 * three headings; the report's own numbers differ for each.
 */
const TRENDS = [
  {
    heading: 'Cryptocurrency Investments.',
    description:
      'Scammers convince victims to deposit more and more money into a fraudulent scheme using a progressive financial “investments” scheme into cryptocurrency investment.',
    image: `${IMG}/bitcoin-glow.jpeg`,
    alt: 'A gold bitcoin among glowing bokeh lights',
    imageFirst: false,
    takeaways: [
      '61,559 Complaints',
      '$7.228 Billion stolen',
      '+ 48% jump in complaints from 2024',
      '+ 25% increase in losses from 2024',
    ],
    rows: [
      ['Under 20', '629', '$12.5 Million'],
      ['20 – 29', '4,627', '$117.3 Million'],
      ['30 – 39', '8,576', '$412.6 Million'],
      ['40 – 49', '10,750', '$924.6 Million'],
      ['50 – 59', '9,856', '$1.38 Billion'],
      ['60+', '13,685', '$2.76 Billion'],
    ],
  },
  {
    heading: 'Cryptocurrency ATMs / Kiosks.',
    description:
      'Criminals trick victims into using a crypto kiosk to send money directly to the scammer’s wallet, using a QR code to hide the real recipient address.',
    image: `${IMG}/crypto-atm-kiosk.jpeg`,
    alt: 'A cryptocurrency ATM kiosk',
    imageFirst: true,
    takeaways: [
      '13,460 Complaints',
      '$389 Million in losses',
      '+ 23% increase in complaints from 2024',
      '+ 58% increase in losses from 2024',
    ],
    rows: [
      ['Under 20', '58', '$124,013'],
      ['20 – 29', '825', '$6.4 Million'],
      ['30 – 39', '1,275', '$10.9 Million'],
      ['40 – 49', '1,472', '$20.8 Million'],
      ['50 – 59', '1,524', '$44.5 Million'],
      ['60+', '6,188', '$257.4 Million'],
    ],
  },
  {
    heading: 'Recovery.',
    description:
      'Scammers target victims of previous crypto fraud, promising to recover lost funds in exchange for upfront fees or sensitive information, but they almost always steal more money instead.',
    image: `${IMG}/broken-bitcoin.jpeg`,
    alt: 'A cracked gold bitcoin on a fractured surface',
    imageFirst: false,
    takeaways: [
      '10,516 Complaints',
      '$1.4 Billion in losses',
      'Rising impersonation of government officials and recovery firms',
    ],
    rows: [
      ['Under 20', '106', '$3.8 Million'],
      ['20 – 29', '841', '$22.4 Million'],
      ['30 – 39', '1,511', '$76.1 Million'],
      ['40 – 49', '1,977', '$194.6 Million'],
      ['50 – 59', '1,706', '$298.2 Million'],
      ['60+', '2,529', '$540.5 Million'],
    ],
    footnote:
      'Losses may also include losses experienced from previous scams which prompted contact with the recovery company.',
  },
];

// Horizontal room the left column gives up to the divider: md:pr-10.
const LEFT_COLUMN_GUTTER = 40;

const TrendBlock = ({ trend }) => {
  // 10-29: the heading and its sentence stay inside the left column (left of
  // the vertical rule) for as long as the heading fits there on one line.
  // Once the column is too narrow for the heading, both run the full width.
  // Measured rather than breakpointed because the headings differ in length
  // ("/ Recovery." fits where "/ Cryptocurrency ATMs / Kiosks." does not).
  const gridRef = useRef(null);
  const probeRef = useRef(null);
  const [confine, setConfine] = useState(false);

  useLayoutEffect(() => {
    const grid = gridRef.current;
    const probe = probeRef.current;
    if (!grid || !probe) return undefined;

    const check = () => {
      const twoColumns = window.matchMedia('(min-width: 768px)').matches;
      const column = grid.clientWidth / 2 - LEFT_COLUMN_GUTTER;
      setConfine(twoColumns && probe.scrollWidth <= column);
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(grid);
    return () => observer.disconnect();
  }, [trend.heading]);

  const panel = (
    <div>
      <p className="text-[15px] text-black md:text-[17px]">Takeaways</p>
      <ul className="mt-1 space-y-1">
        {trend.takeaways.map((item) => (
          <li key={item} className="text-[15px] text-black md:text-[17px]">
            &ndash; {item}
          </li>
        ))}
      </ul>

      <div className="my-5 h-px w-full bg-black/25" />

      <table className="w-full text-[15px] md:text-[17px]">
        <thead>
          <tr className="text-black">
            <th className="pb-1 text-left font-normal underline">Age</th>
            <th className="pb-1 text-center font-normal underline">Count</th>
            <th className="pb-1 text-right font-normal underline">$$$ Loss</th>
          </tr>
        </thead>
        <tbody>
          {trend.rows.map((row) => (
            <tr key={row[0]} className="text-black">
              <td className="py-0.5 text-left">{row[0]}</td>
              <td className="py-0.5 text-center">{row[1]}</td>
              <td className="py-0.5 text-right">{row[2]}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {trend.footnote && (
        <p className="mt-3 text-[12px] leading-snug text-black/60 md:text-[13px]">
          {trend.footnote}
        </p>
      )}
    </div>
  );

  const picture = (
    <img
      src={trend.image}
      alt={trend.alt}
      className="h-[240px] w-full object-cover small:aspect-[161/95] small:h-auto"
    />
  );

  return (
    <PageSection className="pt-12">
      <div className={`relative ${confine ? 'md:w-1/2 md:pr-10' : ''}`}>
        <SectionHeading>{trend.heading}</SectionHeading>
        {/* Invisible single-line copy of the heading, in its exact type, so
            the effect above can read how wide the heading wants to be. */}
        <span
          ref={probeRef}
          aria-hidden="true"
          className="invisible absolute left-0 top-0 whitespace-nowrap text-[24px] font-bold leading-tight md:text-[34px]"
        >
          / {trend.heading}
        </span>
        {/* 10-1: what the scam is, in a sentence, before the figures. */}
        {trend.description && (
          <p className="mt-3 max-w-[70ch] text-[15px] font-light leading-snug text-black md:text-[18px]">
            {trend.description}
          </p>
        )}
      </div>
      {/* 10-28: a vertical rule keeps the two halves apart on desktop; the
          text half stays left-aligned. */}
      <div ref={gridRef} className="mt-5 grid grid-cols-1 gap-y-8 md:grid-cols-2 md:gap-y-0">
        <div className="md:pr-10">{trend.imageFirst ? picture : panel}</div>
        <div className="md:border-l md:border-black/25 md:pl-10">
          {trend.imageFirst ? panel : picture}
        </div>
      </div>
    </PageSection>
  );
};

const BadDream = () => (
  <SubPage title="Bad Dream">
    <PageTitleBox>Show me the MONEY.</PageTitleBox>

    {TRENDS.map((trend) => (
      <TrendBlock key={trend.heading} trend={trend} />
    ))}

    <PageSection className="pt-8">
      <SourceLine>
        Source: FBI 2025 Internet Crime Report &mdash; Cryptocurrency Fraud Trends (p.53). Age
        bands cover complainants who reported an age.
      </SourceLine>
    </PageSection>

    {/* Slide 62. The deck reads "Once of prevention"; set here as the
        proverb it is quoting. */}
    <ClosingBrand tagline="An ounce of prevention is worth a pound of cure." />
  </SubPage>
);

export default BadDream;
